import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import PageHead from "@/components/PageHead";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Vault created");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHead title="Create your vault | ChronoVaults" noindex />
    <div className="min-h-[calc(100vh-88px)] flex items-center justify-center p-8">
      <form onSubmit={submit} className="w-full max-w-lg" data-testid="register-form">
        <p className="label-mono text-terracotta">Create your vault</p>
        <h1 className="font-serif text-4xl md:text-5xl text-bone mt-4 mb-2 leading-tight">
          A private key<br /> to future memories.
        </h1>
        <p className="text-sm text-white/50 mt-4 mb-10">
          Your account is your sender identity. The phone number here is what your recipients will match against later.
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="label-mono mb-3 block">Full name</Label>
            <Input required value={form.name} onChange={set("name")} data-testid="reg-name" className="h-12" />
          </div>
          <div className="col-span-2">
            <Label className="label-mono mb-3 block">Email</Label>
            <Input type="email" required value={form.email} onChange={set("email")} data-testid="reg-email" className="h-12" />
          </div>
          <div>
            <Label className="label-mono mb-3 block">Phone</Label>
            <Input
              required
              placeholder="+15551234567"
              value={form.phone}
              onChange={set("phone")}
              data-testid="reg-phone"
              className="h-12"
            />
          </div>
          <div>
            <Label className="label-mono mb-3 block">Password</Label>
            <Input
              type="password"
              required
              minLength={6}
              value={form.password}
              onChange={set("password")}
              data-testid="reg-password"
              className="h-12"
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={loading}
          data-testid="register-submit"
          className="mt-8 w-full h-12 bg-terracotta hover:bg-terracotta/90 text-white rounded-sm tracking-wide"
        >
          {loading ? "Forging vault..." : "Create my vault"}
        </Button>

        <p className="mt-6 text-sm text-white/50">
          Already sealed one?{" "}
          <Link to="/login" className="text-terracotta hover:underline" data-testid="link-login">
            Sign in
          </Link>
        </p>
      </form>
    </div>
    </>
  );
}
